import * as Sentry from "@sentry/browser";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { Userpilot } from "userpilot";
import { useCurrentUserQuery } from "../../hooks/useCurrentUserQuery";
import { useSiteCategoriesSummary } from "../../hooks/useSiteCategoriesSummary";
import { StartPage } from "../../modules/StartPage/StartPage";
import { PUBLIC_ROUTES } from "../../routes";
import { UserLicense } from "../../utils/enums";
import { AdminDashboard } from "../AdminDashboard/AdminDashboard";
import { ApiSettingsView } from "../ApiSettings/ApiSettingsView";
import { DirektvermarktungView } from "../Direktvermarktung/DirektvermarktungView";
import { Imprint } from "../Imprint/Imprint";
import { Login } from "../Login/Login";
import { OtpConfirm } from "../Login/OtpConfirm";
import { ManagerDashboard } from "../ManagerDashboard/ManagerDashboard";
import { PasswordReset } from "../Password/PasswordReset";
import { PasswordResetConfirm } from "../Password/PasswordResetConfirm";
import { SetPasswordConfirm } from "../Password/SetPasswordConfirm";
import {
  DEFAULT_SUBPAGE as DEFAULT_INVOICING_SUBPAGE,
  PPAInvoicingView
} from "../PPAInvoicing/PPAInvoicingView";
import { PrivacyStatement } from "../PrivacyStatement/PrivacyStatement";
import { ProductSelectPage } from "../ProductSelectPage/ProductSelectPage";
import { RevenueMonitoringView } from "../RevenueMonitoring/RevenueMonitoringView";
import { Settings } from "../Settings/Settings";
import { SiteSetupAssistant } from "../SiteSetupAssistant/SiteSetupAssistant";
import { SupportPage } from "../Support/SupportPage";
import { InternalPage } from "./InternalPage";
import { ManagerStructureViewRoute } from "./ManagerStructureViewRoute";
import { ManagerVariantRoutes } from "./ManagerVariantRoutes";
import { OldManagerStructureViewRoute } from "./OldManagerStructureViewRoute";
import { RedirectToAnalyzer } from "./RedirectToAnalyzer";
import { ShowStaffViewContext } from "./ShowStaffViewContext";
import { loadUserpilot } from "./utils/loadUserpilot";

declare global {
  interface Window {
    _hsq?: {
      push: (command: [string, { [key: string]: string | number }]) => void;
    };
  }
}

function Page() {
  const location = useLocation();

  const [showStaffView, setShowStaffView] = useState<boolean>();
  const { currentUser } = useCurrentUserQuery();
  const { siteCategoriesSummary } = useSiteCategoriesSummary();

  const userLicenses: UserLicense[] | null = currentUser ? [] : null;

  if (currentUser?.has_analyzer_license) {
    userLicenses?.push(UserLicense.Analyzer);
  }

  if (currentUser?.has_manager_license) {
    userLicenses?.push(UserLicense.Manager);
  }

  const toggleShowStaffView = useCallback(
    () => setShowStaffView((oldShowStaffView) => !oldShowStaffView),
    []
  );

  useEffect(() => {
    if (
      typeof showStaffView === "undefined" &&
      typeof currentUser !== "undefined"
    ) {
      setShowStaffView(currentUser?.is_staff || false);
    }
  }, [currentUser, showStaffView]);

  useEffect(() => {
    sessionStorage.removeItem("meter_params_polling_data"); // clean all meter params polling loading data in case when user decides to reload the page
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      Sentry.setUser({
        id: currentUser.id.toString(),
        is_staff: currentUser.is_staff
      });
    } else {
      Sentry.setUser(null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (siteCategoriesSummary) {
      if (currentUser?.userpilot_enabled) {
        loadUserpilot(currentUser, siteCategoriesSummary);
      }
    }
  }, [currentUser, siteCategoriesSummary]);

  useEffect(() => {
    if (siteCategoriesSummary && currentUser?.userpilot_enabled) {
      Userpilot.reload();
    }
  }, [currentUser?.userpilot_enabled, location, siteCategoriesSummary]);

  return (
    <ShowStaffViewContext.Provider value={showStaffView}>
      <Routes>
        <Route element={<Login />} path={PUBLIC_ROUTES.login} />
        <Route element={<OtpConfirm />} path={PUBLIC_ROUTES.otpConfirm} />
        <Route element={<PasswordReset />} path={PUBLIC_ROUTES.passwordReset} />
        <Route
          element={<PasswordResetConfirm />}
          path={PUBLIC_ROUTES.passwordResetConfirm}
        />
        <Route
          element={<SetPasswordConfirm />}
          path={PUBLIC_ROUTES.setPasswordConfirm}
        />
        <Route element={<SupportPage />} path={PUBLIC_ROUTES.support} />
        <Route element={<Imprint />} path={PUBLIC_ROUTES.impressum} />
        <Route
          element={<PrivacyStatement />}
          path={PUBLIC_ROUTES.privacyStatement}
        />
        <Route element={<AdminDashboard />} path="admin-dashboard">
          <Route element={<AdminDashboard />} index />
          <Route element={<AdminDashboard />} path="*" />
        </Route>
        <Route
          element={
            <InternalPage
              currentUser={currentUser}
              toggleShowStaffView={toggleShowStaffView}
            />
          }
          path="*"
        >
          <Route
            element={<ProductSelectPage userLicenses={userLicenses} />}
            index
          />
          <Route element={<RedirectToAnalyzer />} path="analyzer">
            <Route element={<RedirectToAnalyzer />} path="*" />
          </Route>
          <Route element={<RedirectToAnalyzer />} path="projekte/:projectId">
            <Route element={<RedirectToAnalyzer />} path="*" />
          </Route>
          <Route path="manager">
            <Route element={<Navigate to="../projekte" />} path="dashboard" />
            <Route element={<StartPage />} path="startseite">
              <Route element={<StartPage />} index />
              <Route element={<StartPage />} path="*" />
            </Route>
            <Route element={<DirektvermarktungView />} path="vermarktung">
              <Route element={<DirektvermarktungView />} index />
              <Route element={<DirektvermarktungView />} path="*" />
            </Route>
            <Route element={<RevenueMonitoringView />} path="erloesmonitoring">
              <Route element={<RevenueMonitoringView />} index />
              <Route element={<RevenueMonitoringView />} path="*" />
            </Route>
            <Route path="ppa-abrechnung">
              <Route
                element={<Navigate replace to={DEFAULT_INVOICING_SUBPAGE} />}
                index
              />
              <Route element={<PPAInvoicingView />} path=":tab">
                <Route element={<PPAInvoicingView />} index />
                <Route element={<PPAInvoicingView />} path="*" />
              </Route>
            </Route>
            <Route path="projekte">
              <Route element={<ManagerDashboard />} index />
              <Route path=":projectId/liegenschaftskonfigurator/:siteSetupProcessId">
                <Route element={<SiteSetupAssistant />} index />
                <Route element={<SiteSetupAssistant />} path="*" />
              </Route>
              <Route
                element={<ManagerStructureViewRoute />}
                path=":projectId/liegenschaften/:siteId/"
              >
                <Route element={<ManagerStructureViewRoute />} index />
                <Route element={<ManagerStructureViewRoute />} path="*" />
              </Route>
              <Route element={<ManagerVariantRoutes />} path=":projectId">
                <Route element={<ManagerVariantRoutes />} index />
                <Route element={<ManagerVariantRoutes />} path="*" />
              </Route>
            </Route>
            <Route
              element={<OldManagerStructureViewRoute />}
              path="liegenschaften/:siteId"
            >
              <Route element={<OldManagerStructureViewRoute />} index />
              <Route element={<OldManagerStructureViewRoute />} path="*" />
            </Route>
          </Route>
          <Route element={<ApiSettingsView />} path="api-einstellungen">
            <Route element={<ApiSettingsView />} index />
            <Route element={<ApiSettingsView />} path="*" />
          </Route>
          <Route element={<Settings />} path="einstellungen" />
        </Route>
      </Routes>
    </ShowStaffViewContext.Provider>
  );
}

export { Page };
